/**
 * Wizard progress saved in this browser so an agent who backs out can resume.
 * The SSN digits are never saved. Every read and write is wrapped: storage can
 * be missing or blocked (private windows), and then there's simply no draft.
 */
export interface A2PWizardDraft {
  v: 1;
  savedAt: string;
  step: number;
  completedSteps: number[];
  registrationType: 'sole_proprietor' | 'low_volume_standard' | null;
  registerForAgency: boolean;
  coversDownline: boolean;
  values: Record<string, any>;
}

const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const key = (userId: string) => `a2p-wizard-draft:${userId}`;

export function readDraft(userId?: string | null): A2PWizardDraft | null {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as A2PWizardDraft;
    if (draft?.v !== 1 || !draft.registrationType) return null;
    if (Date.now() - Date.parse(draft.savedAt) > MAX_AGE_MS) {
      window.localStorage.removeItem(key(userId));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function writeDraft(userId: string | null | undefined, draft: Omit<A2PWizardDraft, 'v' | 'savedAt'>): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const { ssn: _ssn, ...values } = draft.values || {};
    window.localStorage.setItem(
      key(userId),
      JSON.stringify({ ...draft, values, v: 1, savedAt: new Date().toISOString() } satisfies A2PWizardDraft),
    );
  } catch {
    // Storage full or blocked: resuming just won't be offered.
  }
}

export function clearDraft(userId?: string | null): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key(userId));
  } catch {
    /* nothing to clear */
  }
}

const STEP_LABELS: Record<string, Record<number, string>> = {
  sole_proprietor: { 0: 'Registration type', 3: 'Your details', 4: 'Messages' },
  low_volume_standard: { 0: 'Registration type', 1: 'EIN', 2: 'CP 575 letter', 3: 'Business details', 4: 'Messages' },
};

/** "Step 3 of 5, Business details" for the resume prompt. */
export function draftProgress(draft: A2PWizardDraft): string {
  const labels = STEP_LABELS[draft.registrationType || 'low_volume_standard'];
  const order = Object.keys(labels).map(Number);
  const index = Math.max(0, order.indexOf(draft.step));
  return `Step ${index + 1} of ${order.length}, ${labels[order[index]]}`;
}

/** "Visa ending 4242" for the pay button. */
export function cardLabel(card: { brand: string; last4: string }): string {
  const names: Record<string, string> = { amex: 'Amex', american_express: 'Amex', mastercard: 'Mastercard', diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay' };
  const brand = names[card.brand] || card.brand.charAt(0).toUpperCase() + card.brand.slice(1);
  return `${brand} ending ${card.last4}`;
}
